import { motion } from 'motion/react'

import { UseSnackbar } from '@app/context/SnackbarContext'

import { Body2, Caption1 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { Tooltip } from '@ui/components/Tooltip'
import { ArrowCircleIcon } from '@ui/icons/ArrowCircle.icon'
import { StatusReversedIcon } from '@ui/icons/StatusReversed.icon'
import { createValidIcon } from '@ui/icons/utils/type-helper'
import { DateTime } from '@ui/v2/Table/cells/DateTime'
import { VIEW_MORE_ARROW_COL_KEY } from '@ui/v2/Table/hooks/useTableSearch'
import { BaseColumn, BaseColumnCtx } from '@ui/v2/Table/types'

import { formatDate } from '@utils/date.utils'
import { triggerDownload } from '@utils/downloadFile'

import {
  ExportHubDto,
  ExportHubReportFormat,
  ExportHubReportSection,
  ExportHubReportStatus,
  ExportHubReportStatusId,
} from '@model/ExportHub'

import { AnimatedSwitch } from '@shared/animations/AnimatedSwitch'
import { Box } from '@shared/components/Box'
import { ExportHubStatusIcon } from '@shared/components/StatusIcon'

import { ExportHubTableSortKeys } from './ExportHubTable'

export interface ExportHubColumnCtx extends BaseColumnCtx {
  retryReport: (reportId: string) => void
  isRetryPending: boolean
  addSnackbar: UseSnackbar['addSnackbar']
  isMobile: boolean
}
export type ExportHubColumn = BaseColumn<
  ExportHubDto,
  ExportHubColumnCtx,
  ExportHubTableSortKeys | undefined | typeof VIEW_MORE_ARROW_COL_KEY
>

const DownloadIcon = createValidIcon((props: React.ComponentProps<typeof ArrowCircleIcon.Down>) => (
  <ArrowCircleIcon.Down {...props} size={16} color='text-med-em' />
))

export const exportHubColumns: ExportHubColumn[] = [
  {
    id: 'ReportName',
    labelKey: 'IB.Export.reportName',
    responsiveWidth: { desktop: { minWidth: 204 } },
    visibleOnMobile: true,
    getValue: ({ reportName }, { dateFormat }) => {
      const [name, date] = reportName.split('_')
      const normalizedDate = formatDate(date, { formatType: dateFormat, withTime: false })

      return `${name}_${normalizedDate}`
    },
  },
  {
    id: 'Section',
    labelKey: 'IB.Export.section',
    responsiveWidth: { desktop: { width: 180 } },
    getValue: ({ section }) => ExportHubReportSection[section],
  },
  {
    id: 'CreatedDate',
    labelKey: 'Date',
    responsiveWidth: { desktop: { minWidth: 204 } },
    getValue: ({ createdDate }, { t, dateFormat }) => {
      const [date, time] = formatDate(createdDate, { formatType: dateFormat })?.split(' ') ?? [
        '',
        '',
      ]
      return <DateTime date={date} time={time} t={t} />
    },
  },
  {
    id: 'Format',
    labelKey: 'IB.Export.format',
    visibleOnMobile: true,
    responsiveWidth: { desktop: { minWidth: 141 }, mobile: { width: 70 } },
    getValue: ({ format }) => ExportHubReportFormat[format],
  },
  {
    id: 'Status',
    labelKey: 'IB.Export.status',
    minWidth: 299,
    getValue: ({ status, statusChangedAt }, { t }) => {
      const statusKey = ExportHubReportStatus[status]
      const statusName = t(`IB.Export.statuses.${statusKey}`)

      const statusText =
        status === ExportHubReportStatusId.Expired
          ? t('Reports are stored for 24 hours')
          : formatDate(statusChangedAt, { formatType: 'HH:mm' })

      const isProcessing = status === ExportHubReportStatusId.Processing

      return (
        <Box flex align='center' gap='x8'>
          {isProcessing ? (
            <motion.span
              style={{ display: 'inline-flex' }}
              animate={{ opacity: [1, 0.4, 1], scale: [1, 0.94, 1] }}
              transition={{
                duration: 1.6,
                ease: 'easeInOut',
                repeat: Infinity,
              }}
            >
              <ExportHubStatusIcon status={status} />
            </motion.span>
          ) : (
            <ExportHubStatusIcon status={status} />
          )}

          <Box flex direction='column'>
            <AnimatedSwitch activeKey={status} variant='morph'>
              <Body2 medium color='text-high-em'>
                {statusName}
              </Body2>
            </AnimatedSwitch>

            <Caption1 sensitive color='text-low-em'>
              {statusText}
            </Caption1>
          </Box>
        </Box>
      )
    },
  },
  {
    id: undefined,
    labelKey: '',
    width: 48,
    visibleOnMobile: true,
    visibleOnDesktop: false,
    getValue: ({ status }) => {
      return <ExportHubStatusIcon status={status} />
    },
  },
  {
    id: undefined,
    labelKey: '',
    responsiveWidth: { desktop: { minWidth: 204 }, mobile: { width: 48 } },
    align: 'right',
    visibleOnMobile: true,
    getValue: (
      { id, downloadUrl, canDownload, canRetry },
      { t, retryReport, isRetryPending, addSnackbar, isMobile }
    ) => {
      const icon = canDownload ? DownloadIcon : canRetry ? StatusReversedIcon : null

      if (!icon) {
        return null
      }

      const handleDownload = () => {
        triggerDownload(downloadUrl)
        addSnackbar.info({ message: t('IB.Export.downloadStarted') })
      }

      return (
        <Tooltip
          disabled={(!canDownload && !canRetry) || isMobile}
          content={isRetryPending ? t('In Process') : canDownload ? t('Download') : ''}
        >
          <IconButton.Secondary
            icon={icon}
            size='sm'
            {...(canRetry && !canDownload && { onClick: () => retryReport(id) })}
            {...(canDownload &&
              !canRetry && {
                onClick: handleDownload,
              })}
            disabled={isRetryPending}
          />
        </Tooltip>
      )
    },
  },
  {
    id: VIEW_MORE_ARROW_COL_KEY,
    labelKey: '',
    visibleOnMobile: true,
    align: 'center',
    width: 40,
    getValue: () => null,
  },
]
